import React, { useState, useEffect } from 'react';
import Sidebar from "../../components/Sidebar"; // Assuming these paths are correct
import Topbar from "../../components/Topbar";   // Assuming these paths are correct

// Helper function to format dates (you can customize this)
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const options = { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-GB', options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return 'Invalid Date';
  }
};

// Styles for the user avatar in the table
const tableAvatarStyle = {
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  objectFit: 'cover',
};

const Users = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [currentUsers, setCurrentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10); // Or 5, 15, etc.

  // Assuming your Sidebar has a fixed width, adjust this value if needed
  const sidebarWidth = "250px";

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = sessionStorage.getItem('token'); // Replace 'authToken' with your actual token key
        if (!token) {
          setError("Authentication token not found. Please log in again.");
          setLoading(false);
          return;
        }

        const response = await fetch('https://api.booze247.co.uk/api/admin/users', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: "Unknown server error" }));
          throw new Error(`HTTP error! Status: ${response.status} - ${errorData.message || response.statusText}`);
        }

        const result = await response.json();
        if (result.success && result.data && Array.isArray(result.data.users)) {
          setAllUsers(result.data.users);
        } else {
          throw new Error("Failed to fetch users or data format is incorrect.");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Effect for updating currentUsers when allUsers, currentPage, or usersPerPage changes
  useEffect(() => {
    if (allUsers.length > 0) {
      const indexOfLastUser = currentPage * usersPerPage;
      const indexOfFirstUser = indexOfLastUser - usersPerPage;
      setCurrentUsers(allUsers.slice(indexOfFirstUser, indexOfLastUser));
    } else {
      setCurrentUsers([]); // Ensure currentUsers is empty if allUsers is empty
    }
  }, [allUsers, currentPage, usersPerPage]);

  // Pagination logic
  const totalPages = Math.ceil(allUsers.length / usersPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleUsersPerPageChange = (event) => {
    setUsersPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Calculate the starting serial number for the current page
  const startingSerialNumber = (currentPage - 1) * usersPerPage + 1;

  return (
    <div>
      <Topbar />
      <Sidebar />

      <main style={{ marginLeft: sidebarWidth, padding: '1.5rem', paddingTop: '70px' /* Adjust if Topbar is fixed */ }}>
        <div className="container-fluid">
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 120px)' }}> {/* Adjusted height */}
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="ms-3 fs-5">Loading Users...</p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mx-3" role="alert">
              <h4 className="alert-heading">Error Fetching Users!</h4>
              <p>{error}</p>
              <hr />
              <p className="mb-0">Please check the console for more details or try again later.</p>            
            </div>
          )}

          {!loading && !error && (
            <div className="card shadow-sm">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  User List <span className="badge bg-secondary ms-1">{allUsers.length} Total</span>
                </h5>
                <div className="d-flex align-items-center">
                  <label htmlFor="usersPerPageSelect" className="form-label me-2 mb-0 small">Rows:</label>
                  <select 
                    id="usersPerPageSelect"
                    className="form-select form-select-sm" 
                    value={usersPerPage} 
                    onChange={handleUsersPerPageChange}
                    style={{width: 'auto'}}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="15">15</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
              <div className="card-body">
                {currentUsers.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover table-bordered align-middle">
                      <thead className="table-primary"> {/* Using table-primary for header */}
                        <tr>
                          <th scope="col" className="text-center" style={{width: '5%'}}>#</th>
                          <th scope="col" className="text-center" style={{width: '10%'}}>Photo</th>
                          <th scope="col" style={{width: '25%'}}>Full Name</th>
                          <th scope="col" style={{width: '25%'}}>Email</th>
                          <th scope="col" style={{width: '15%'}}>Phone</th>
                          <th scope="col" className="text-center" style={{width: '20%'}}>Joined At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentUsers.map((user, index) => (
                          <tr key={user._id || user.email}> {/* Use a unique key */}
                            <td className="text-center fw-bold">{startingSerialNumber + index}</td>
                            <td className="text-center">
                              {user.photo ? (
                                <img src={user.photo} alt={user.fullname} style={tableAvatarStyle} className="img-thumbnail p-0" />
                              ) : (
                                <div style={{...tableAvatarStyle, lineHeight: '40px'}} className="bg-secondary text-white d-inline-flex align-items-center justify-content-center">
                                  {user.fullname ? user.fullname.charAt(0).toUpperCase() : <i className="bi bi-person-fill"></i>}
                                </div>
                              )}
                            </td>
                            <td>{user.fullname || <span className="text-muted fst-italic">N/A</span>}</td>
                            <td>{user.email || <span className="text-muted fst-italic">N/A</span>}</td>
                            <td className="text-nowrap">{user.phone || <span className="text-muted fst-italic">N/A</span>}</td>
                            <td className="text-center text-nowrap small">{formatDate(user.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-light text-center py-4" role="alert">
                     {allUsers.length === 0 ? "No users found." : "No users on this page."}
                  </div>
                )}
              </div>
              {totalPages > 1 && (
                <div className="card-footer bg-light d-flex justify-content-between align-items-center">
                    <span className="text-muted small">
                        Page {currentPage} of {totalPages} (Showing {currentUsers.length} of {allUsers.length} users)
                    </span>
                  <nav aria-label="User pagination">
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(1)} aria-label="First">
                          <span aria-hidden="true">&laquo;&laquo;</span>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage - 1)} aria-label="Previous">
                          <span aria-hidden="true">&laquo;</span>
                        </button>
                      </li>
                      {/* Dynamic Page Numbers - simplified for brevity, can be expanded */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => page === 1 || page === totalPages || (page >= currentPage -1 && page <= currentPage + 1)) // Basic logic for showing limited page numbers
                        .map((page, index, arr) => (
                            <React.Fragment key={page}>
                                {index > 0 && arr[index-1] !== page -1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                                <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => paginate(page)}>{page}</button>
                                </li>
                            </React.Fragment>
                        ))
                      }
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(currentPage + 1)} aria-label="Next">
                          <span aria-hidden="true">&raquo;</span>
                        </button>
                      </li>
                       <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button className="page-link" onClick={() => paginate(totalPages)} aria-label="Last">
                          <span aria-hidden="true">&raquo;&raquo;</span>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Make sure Bootstrap icons are linked if you use them (e.g., for placeholder avatar) */}
      {/* <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css"> */}
      <style jsx global>{`
        main {
            padding-top: 70px; /* Adjust this based on your Topbar's height */
            background-color: #f8f9fa; /* Light background for the main content area */
            min-height: 100vh;
        }
        .table-hover tbody tr:hover {
            background-color: #e9ecef; /* Slightly darker hover for better visibility */
        }
        .card-header h5 {
            font-weight: 500; /* Medium weight for card titles */
        }
        .pagination .page-item.active .page-link {
            background-color: #0d6efd; /* Bootstrap primary color */
            border-color: #0d6efd;
        }
        .img-thumbnail.p-0 { /* To remove padding from thumbnail if avatar is an image */
            padding: 0 !important;
        }
        .badge.bg-secondary {
            font-size: 0.8em;
        }
      `}</style>
    </div>
  );
};

export default Users;